/**
 * App.js – router + auth gate.
 *
 * IMPORTANT: When the URL contains `#session_id=...` (Emergent Google OAuth
 * callback), we render <AuthCallback /> FIRST so it can exchange the session
 * before any ProtectedRoute tries /auth/me.
 */
import '@/App.css';
import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/context/AuthContext';

/**
 * ScrollToTop – global route-change scroll restorer.
 *
 * React Router v6 does not auto-scroll to the top when the path changes
 * (it intentionally preserves browser scroll for back/forward). For a
 * mobile-first app where every page is a fresh "screen", users expect to
 * land at the top of every navigation — especially when clicking the
 * "See how it works" tour from a scrolled-down welcome screen. We scroll
 * the window AND the inner `.lf-shell` container (since that's the scroll
 * area on mobile), and skip restoration when the user navigated via
 * browser back/forward (POP) so anchor history still feels natural.
 */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    // Some pages render their scroll inside .lf-shell rather than window.
    const shell = document.querySelector('.lf-shell');
    if (shell) shell.scrollTop = 0;
  }, [pathname]);
  return null;
}

import WelcomePage from '@/pages/WelcomePage';
import AuthPage from '@/pages/AuthPage';
import AuthCallback from '@/pages/AuthCallback';
import OnboardingPage from '@/pages/OnboardingPage';
import HomePage from '@/pages/HomePage';
import ScenariosPage from '@/pages/ScenariosPage';
import LiveSessionPage from '@/pages/LiveSessionPage';
import RepeatPracticePage from '@/pages/RepeatPracticePage';
import ReviewPage from '@/pages/ReviewPage';
import PricingPage from '@/pages/PricingPage';
import AdminPromoPage from '@/pages/AdminPromoPage';
import VocabularyPage from '@/pages/VocabularyPage';
import ProfilePage from '@/pages/ProfilePage';
import PrepScreen from '@/pages/PrepScreen';
import NewcomerPage from '@/pages/NewcomerPage';
import FoundationsListPage from '@/pages/FoundationsListPage';
import FoundationLessonPage from '@/pages/FoundationLessonPage';
import GuidePage from '@/pages/GuidePage';
import UnsubscribePage from '@/pages/UnsubscribePage';
import PrivacyPage from '@/pages/PrivacyPage';
import AccessibilityPage from '@/pages/AccessibilityPage';
import TeacherLessonPreview from '@/pages/TeacherLessonPreview';
import TeacherLessonRunPage from '@/pages/TeacherLessonRunPage';
import TeacherLessonRecapPage from '@/pages/TeacherLessonRecapPage';
import TeacherNotebookPage from '@/pages/TeacherNotebookPage';

function LoadingShell() {
  return (
    <div className="lf-shell flex items-center justify-center">
      <div className="text-[var(--lf-text-muted)] text-sm">Loading LingoFlow…</div>
    </div>
  );
}

function RequireAuth({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <LoadingShell />;
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  if (user.onboarding_status !== 'complete' && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }
  return children;
}

function RequireGuest({ children }) {
  const { user } = useAuth();
  if (user === null) return <LoadingShell />;
  if (user && typeof user === 'object') {
    return <Navigate to={user.onboarding_status === 'complete' ? '/home' : '/onboarding'} replace />;
  }
  return children;
}

function HashCallbackGate({ children }) {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return children;
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          {/* Skip-to-content link — WCAG 2.4.1. Visually hidden until a
              keyboard user tabs into it; lets screen-reader / keyboard-only
              users bypass the persistent header on every page. */}
          <a href="#main-content" className="lf-skip-link" data-testid="skip-to-content">
            Skip to content
          </a>
          <main id="main-content" tabIndex={-1}>
            <HashCallbackGate>
            <Routes>
              <Route path="/" element={<RequireGuest><WelcomePage /></RequireGuest>} />
              <Route path="/login" element={<RequireGuest><AuthPage mode="login" /></RequireGuest>} />
              <Route path="/register" element={<RequireGuest><AuthPage mode="register" /></RequireGuest>} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/guide" element={<GuidePage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/accessibility" element={<AccessibilityPage />} />
              <Route path="/support" element={<PrivacyPage />} />

              <Route path="/onboarding" element={<RequireAuthAllowOnboarding><OnboardingPage /></RequireAuthAllowOnboarding>} />
              <Route path="/home" element={<RequireAuth><HomePage /></RequireAuth>} />
              <Route path="/newcomer" element={<RequireAuth><NewcomerPage /></RequireAuth>} />
              <Route path="/foundations" element={<RequireAuth><FoundationsListPage /></RequireAuth>} />
              <Route path="/foundations/:id" element={<RequireAuth><FoundationLessonPage /></RequireAuth>} />
              <Route path="/teacher/today" element={<RequireAuth><TeacherLessonPreview today /></RequireAuth>} />
              <Route path="/teacher/lesson/:lessonId" element={<RequireAuth><TeacherLessonPreview /></RequireAuth>} />
              <Route path="/teacher/run/:runId" element={<RequireAuth><TeacherLessonRunPage /></RequireAuth>} />
              <Route path="/teacher/recap/:runId" element={<RequireAuth><TeacherLessonRecapPage /></RequireAuth>} />
              <Route path="/teacher/notebook" element={<RequireAuth><TeacherNotebookPage /></RequireAuth>} />
              <Route path="/unsubscribe" element={<RequireAuth><UnsubscribePage /></RequireAuth>} />
              <Route path="/prep" element={<RequireAuth><PrepScreen /></RequireAuth>} />
              <Route path="/scenarios" element={<RequireAuth><ScenariosPage /></RequireAuth>} />
              <Route path="/session/:sessionId" element={<RequireAuth><LiveSessionPage /></RequireAuth>} />
              <Route path="/repeat" element={<RequireAuth><RepeatPracticePage /></RequireAuth>} />
              <Route path="/repeat/:sessionId" element={<RequireAuth><RepeatPracticePage /></RequireAuth>} />
              <Route path="/review/:sessionId" element={<RequireAuth><ReviewPage /></RequireAuth>} />
              <Route path="/vocabulary" element={<RequireAuth><VocabularyPage /></RequireAuth>} />
              <Route path="/profile" element={<RequireAuth><ProfilePage /></RequireAuth>} />
              <Route path="/pricing" element={<RequireAuth><PricingPage /></RequireAuth>} />
              <Route path="/admin/promo" element={<RequireAuth><AdminPromoPage /></RequireAuth>} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </HashCallbackGate>
          </main>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

function RequireAuthAllowOnboarding({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <LoadingShell />;
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  return children;
}
